import React from 'react';
import { useTranslation } from 'react-i18next';

const RefundPolicyPage: React.FC = () => {
  const { i18n } = useTranslation();
  const isZh = i18n.language?.startsWith('zh');

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 text-[#23272f] dark:text-gray-200 transition-colors">
      <div className="max-w-3xl mx-auto py-8 sm:py-10 px-4 sm:px-6">
        {isZh ? (
          <>
            <div className="border-b border-gray-200 dark:border-gray-800 pb-4 mb-6">
              <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white">
                退款政策 (Refund Policy)
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                最后更新：{new Date().toLocaleDateString('zh-CN')} · 14 天无理由全额退款保障
              </p>
            </div>

            <div className="space-y-5 text-sm sm:text-base leading-relaxed text-gray-700 dark:text-gray-300">
              <section>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                  1. 14 天无理由全额退款保障
                </h2>
                <p>
                  AtlasSlicer 采用一次性终身授权制。自购买之日起 <strong>14 个自然日内</strong>，若因任何原因对软件不满意或遇到引擎兼容问题，均可申请 100% 全额退款，由 Paddle 官方渠道原路退回。退款完成后，对应激活码将自动停用。
                </p>
              </section>

              <section>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                  2. 申请方式与所需信息
                </h2>
                <p className="mb-2">
                  为核实订单所有权，<strong>请务必使用您购买时填写的邮箱</strong>直接发送邮件至官方客服邮箱：
                  <a
                    href="mailto:support@atlasslicer.com"
                    className="ml-1 font-bold text-orange-600 dark:text-orange-400 hover:underline"
                  >
                    support@atlasslicer.com
                  </a>
                  ，并在邮件中附带以下信息：
                </p>
                <ul className="list-disc pl-5 space-y-1 text-sm sm:text-base text-gray-600 dark:text-gray-400">
                  <li><strong>发信邮箱（必须）</strong>：请使用接收激活码与 Paddle 账单的同一购买邮箱直接发信；</li>
                  <li><strong>Paddle 订单号 / 交易号</strong>（收据邮件中的 Order ID，如 #12345678）；</li>
                  <li><strong>退款原因</strong>（选填，帮助我们持续优化改进产品）。</li>
                </ul>
                <p className="mt-2.5 text-xs sm:text-sm text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 px-3 py-1.5 rounded-lg border border-amber-200 dark:border-amber-900/50">
                  ⚠️ <strong>安全提示</strong>：为保障您的账户与软件授权安全，我们仅受理直接由原购买邮箱发出的退款申请。
                </p>
              </section>

              <section>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                  3. 审核与到账时效
                </h2>
                <p>
                  我们将在 <strong>1~2 个工作日内</strong> 审核通过并原路提交退款。款项将原路返还至原支付方式（信用卡、PayPal、银联卡等），通常会在 <strong>3~7 个工作日内</strong> 到账。
                </p>
              </section>

              <div className="pt-2 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-800">
                如有任何疑问或技术配置咨询，可随时联系官方邮箱：
                <a
                  href="mailto:support@atlasslicer.com"
                  className="ml-1 font-medium text-orange-600 dark:text-orange-400 hover:underline"
                >
                  support@atlasslicer.com
                </a>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="border-b border-gray-200 dark:border-gray-800 pb-4 mb-6">
              <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white">
                Refund Policy
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                Last Updated: {new Date().toLocaleDateString('en-US')} · 14-Day Money-Back Guarantee
              </p>
            </div>

            <div className="space-y-5 text-sm sm:text-base leading-relaxed text-gray-700 dark:text-gray-300">
              <section>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                  1. 14-Day Full Money-Back Guarantee
                </h2>
                <p>
                  AtlasSlicer is sold as a lifetime purchase. If you are not satisfied with the software or encounter compatibility issues in your workflow, you can request a 100% full refund within <strong>14 calendar days</strong> of your initial purchase date. Once refunded, your license key will be deactivated.
                </p>
              </section>

              <section>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                  2. How to Request a Refund
                </h2>
                <p className="mb-2">
                  To verify order ownership, <strong>please send the request directly from the email address used during purchase</strong> to our support team:
                  <a
                    href="mailto:support@atlasslicer.com"
                    className="ml-1 font-bold text-orange-600 dark:text-orange-400 hover:underline"
                  >
                    support@atlasslicer.com
                  </a>
                  , with the following details:
                </p>
                <ul className="list-disc pl-5 space-y-1 text-sm sm:text-base text-gray-600 dark:text-gray-400">
                  <li><strong>Sender Email (Required)</strong>: Must match the original purchase email that received the license key and Paddle receipt;</li>
                  <li><strong>Paddle Order ID / Transaction ID</strong> (found in your receipt email, e.g. #12345678);</li>
                  <li><strong>Reason for refund</strong> (optional, helps us improve future versions).</li>
                </ul>
                <p className="mt-2.5 text-xs sm:text-sm text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 px-3 py-1.5 rounded-lg border border-amber-200 dark:border-amber-900/50">
                  ⚠️ <strong>Security Notice</strong>: To protect your purchase and license security, refund requests can only be accepted when sent directly from your original purchase email.
                </p>
              </section>

              <section>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                  3. Processing & Settlement Time
                </h2>
                <p>
                  We inspect and approve refund requests within <strong>1-2 business days</strong>. Refunds are issued directly back to your original payment method via Paddle, and funds typically appear on your statement within <strong>3 to 7 business days</strong>.
                </p>
              </section>

              <div className="pt-2 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-800">
                Questions or technical inquiries? Contact us at:
                <a
                  href="mailto:support@atlasslicer.com"
                  className="ml-1 font-medium text-orange-600 dark:text-orange-400 hover:underline"
                >
                  support@atlasslicer.com
                </a>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RefundPolicyPage;
